import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Home,
  Users,
  Heart,
  GraduationCap,
  Briefcase,
  ArrowRight,
  CheckCircle,
  Sparkles,
  Clock,
  MapPin,
  MessageCircle,
  Calendar,
} from "lucide-react";

const services = [
  {
    id: "accommodation",
    icon: Home,
    title: "Supported Accommodation",
    subtitle: "Safe housing with structured support",
    description:
      "Our supported accommodation provides young people with a safe, stable living environment combined with tailored support to develop independence skills.",
    features: [
      "Regular keywork sessions",
      "Life skills development",
      "Independence preparation",
      "Emotional wellbeing support",
      "Professional liaison",
      "24/7 support availability",
    ],
    image:
      "https://images.unsplash.com/photo-1758315417336-38777d626815?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    color: "bg-blue-500",
  },
  {
    id: "mentoring",
    icon: Users,
    title: "Mentoring & Programmes",
    subtitle: "Structured support programmes",
    description:
      "Our mentoring programmes provide consistent, relationship-based support that helps young people build confidence, skills and positive connections.",
    features: [
      "One-to-one mentoring",
      "Group programmes",
      "Visionary Programme (female-focused)",
      "Skills workshops",
      "Peer support opportunities",
      "Goal-setting and reviews",
    ],
    image:
      "https://images.unsplash.com/photo-1758525225856-d837707cb13f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    color: "bg-teal-500",
    highlight: "Visionary Programme",
  },
  {
    id: "wraparound",
    icon: Heart,
    title: "Wraparound Support",
    subtitle: "Comprehensive holistic care",
    description:
      "Our wraparound support ensures young people receive comprehensive care that addresses all aspects of their wellbeing and development.",
    features: [
      "Advocacy support",
      "Education engagement",
      "Appointment accompaniment",
      "Community integration",
      "Family mediation support",
      "Benefits and housing guidance",
    ],
    image:
      "https://images.unsplash.com/photo-1741699428220-65f37f3fbbcb?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
    color: "bg-purple-500",
  },
];

const keyworkAreas = [
  { icon: GraduationCap, title: "Education & Training" },
  { icon: Briefcase, title: "Employment Support" },
  { icon: Heart, title: "Emotional Wellbeing" },
  { icon: Home, title: "Independent Living Skills" },
  { icon: Users, title: "Relationships & Boundaries" },
  { icon: MapPin, title: "Community Engagement" },
];

export default function ServicesPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <div className="blob-teal -top-40 -right-40" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                Our Services
              </div>
              <h1
                className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-6"
                data-testid="services-headline"
              >
                Comprehensive support for{" "}
                <span className="text-teal-600">every journey</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                We offer a range of services designed to meet young people where
                they are and support them towards where they want to be.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`section-padding ${index % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="container-custom">
            <div
              className={`grid lg:grid-cols-2 gap-16 items-center ${
                index % 2 === 1 ? "lg:flex-row-reverse" : ""
              }`}
            >
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? "lg:order-2" : ""}
              >
                <div
                  className={`w-12 h-12 ${service.color} rounded-xl flex items-center justify-center text-white mb-6`}
                >
                  <service.icon className="w-6 h-6" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                  {service.title}
                </h2>
                <p className="text-teal-600 font-medium mb-4">
                  {service.subtitle}
                </p>
                <p className="text-slate-600 leading-relaxed mb-8">
                  {service.description}
                </p>

                {service.highlight && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 text-amber-800 font-medium mb-1">
                      <Sparkles className="w-5 h-5" />
                      {service.highlight}
                    </div>
                    <p className="text-amber-700 text-sm">
                      A dedicated support strand focused on empowering young
                      women through tailored mentoring and group work.
                    </p>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {service.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link to="/referrals">
                  <Button
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
                    data-testid={`service-referral-btn-${service.id}`}
                  >
                    Discuss a Referral
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? "lg:order-1" : ""}
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="rounded-3xl shadow-lg w-full h-[400px] object-cover"
                  data-testid={`service-image-${service.id}`}
                />
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Keywork Areas */}
      <section className="section-padding bg-slate-900 text-white" data-testid="keywork-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Keywork Support Areas
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Our keywork sessions cover a wide range of areas to support holistic development
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {keyworkAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-2xl p-6 flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <area.icon className="w-6 h-6 text-teal-400" />
                </div>
                <span className="font-medium">{area.title}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700" data-testid="services-cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to explore our services?
          </h2>
          <p className="text-teal-100 text-lg mb-8 max-w-xl mx-auto">
            Contact us to discuss how we can support a young person in your care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/referrals">
              <Button
                size="lg"
                className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8"
                data-testid="services-referral-btn"
              >
                Make a Referral
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-8"
                data-testid="services-contact-btn"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
